import { useCallback } from 'react';

import { MutationResult } from '@apollo/react-common';
import { useMutation } from '@apollo/react-hooks';
import { ExecutionResult } from 'graphql';
import getStravaSettings from '../../components/StravaSettingsModals/StravaSettingsModal/stravaSettings.gql';
import {
  FitnessApplicationSettingsInput,
  SetFitnessApplicationSettingsMutation,
  SetFitnessApplicationSettingsMutationVariables,
  GetStravaSettingsQuery,
  GetStravaSettingsQueryVariables,
  PageType,
  Page,
  FitnessApplication,
} from '../../../types/__generated__';
import { PageMetaContext } from '../../context/PageMetaContext';
import { useNotNullableContext } from '../useNotNullableContext';
import { DataProxy } from 'apollo-cache';
import mutation from './queries/setFitnessSettings.gql';

type UpdateStravaSettings = (
  input: FitnessApplicationSettingsInput,
) => Promise<ExecutionResult<SetFitnessApplicationSettingsMutation>>;

export default function useSaveStravaSettings(): [
  UpdateStravaSettings,
  MutationResult<SetFitnessApplicationSettingsMutation>,
] {
  const { pageType, pageShortName } = useNotNullableContext(PageMetaContext);

  const [runMutation, mutationResult] = useMutation<
    SetFitnessApplicationSettingsMutation,
    SetFitnessApplicationSettingsMutationVariables
  >(mutation, {
    update(cache, { data }) {
      if (!data || !data.setFitnessApplicationSettings) {
        return;
      }

      const result = readStravaSettings(pageType, pageShortName, cache);

      const { previousPage, previousViewer } = result;

      const stravaSettings: FitnessApplication = {
        __typename: 'FitnessApplication',
        settings: data.setFitnessApplicationSettings,
      };

      const newData = {
        viewer: previousViewer,
        page: {
          ...previousPage,
          strava: stravaSettings,
        },
      };

      writeStravaSettings(pageType, pageShortName, newData, cache);
    },
    context: {
      credentials: 'include',
    },
  });

  return [
    useCallback(
      input =>
        runMutation({
          variables: {
            input,
          },
        }),
      [runMutation],
    ),
    mutationResult,
  ];
}

const readStravaSettings = (
  pageType: string,
  pageSlug: string,
  cache: DataProxy,
) => {
  const result = cache.readQuery<
    GetStravaSettingsQuery,
    GetStravaSettingsQueryVariables
  >({
    query: getStravaSettings,
    variables: {
      type: pageType.toUpperCase() as PageType,
      slug: pageSlug,
    },
  });

  const previousViewer = result?.viewer;
  const previousPage =
    result?.page ??
    ({
      id: '',
      legacyId: '',
      type: pageType.toUpperCase() as PageType,
      slug: pageSlug,
      url: '',
      title: '',
    } as Page);

  return { previousViewer, previousPage };
};

const writeStravaSettings = (
  pageType: string,
  pageSlug: string,
  data: GetStravaSettingsQuery,
  cache: DataProxy,
): void => {
  cache.writeQuery<GetStravaSettingsQuery, GetStravaSettingsQueryVariables>({
    query: getStravaSettings,
    data,
    variables: {
      type: pageType.toUpperCase() as PageType,
      slug: pageSlug,
    },
  });
};
